import { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useFamily } from "./useFamily";
import { toast } from "sonner";

export interface RecurringTaskItem {
  id: string; // occurrence id
  templateId: string;
  dueDate: string;
  status: "pending" | "done" | "skipped";
  title: string;
  titleOverride: string | null;
  description: string | null;
  category: string | null;
  durationSeconds: number;
  completionPoints: number;
  startedAt: string | null;
  doneAt: string | null;
  improvementNote: string | null;
  isRecurring: true;
}

export function useRecurringTasks(childId: string | undefined, activeDate: string) {
  const { familyId } = useFamily();
  const queryClient = useQueryClient();
  const ensuredRef = useRef<string>("");

  // JIT ensure: materialize occurrences for the viewed date
  useEffect(() => {
    if (!childId || !activeDate || activeDate === "all") return;
    const key = `${childId}-${activeDate}`;
    if (ensuredRef.current === key) return;
    ensuredRef.current = key;

    supabase.functions
      .invoke("create-daily-occurrences", {
        body: {
          child_id: childId,
          start_date: activeDate,
          end_date: activeDate,
          include: ["tasks"],
        },
      })
      .then(({ error }) => {
        if (error) console.warn("JIT ensure failed:", error);
        else queryClient.invalidateQueries({ queryKey: ["recurring-tasks", childId, activeDate] });
      });
  }, [childId, activeDate, queryClient]);

  // Query: fetch task_occurrences + task_templates for the date
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["recurring-tasks", childId, activeDate],
    queryFn: async (): Promise<RecurringTaskItem[]> => {
      if (!childId || activeDate === "all") return [];

      const { data: occurrences, error: occErr } = await supabase
        .from("task_occurrences")
        .select("id, template_id, due_date, status, title_override, started_at, done_at, improvement_note")
        .eq("due_date", activeDate)
        .neq("status", "skipped");

      if (occErr) throw occErr;
      if (!occurrences || occurrences.length === 0) return [];

      const templateIds = [...new Set(occurrences.map((o) => o.template_id))];

      const { data: templates, error: tplErr } = await supabase
        .from("task_templates")
        .select("id, title, description, category, duration_seconds, completion_points, child_id")
        .in("id", templateIds)
        .eq("child_id", childId);

      if (tplErr) throw tplErr;

      const tplMap = new Map((templates ?? []).map((t) => [t.id, t]));

      return occurrences
        .filter((o) => tplMap.has(o.template_id))
        .map((o) => {
          const tpl = tplMap.get(o.template_id)!;
          return {
            id: o.id,
            templateId: o.template_id,
            dueDate: o.due_date,
            status: o.status as "pending" | "done" | "skipped",
            title: o.title_override || tpl.title,
            titleOverride: o.title_override,
            description: tpl.description,
            category: tpl.category,
            durationSeconds: tpl.duration_seconds,
            completionPoints: tpl.completion_points,
            startedAt: o.started_at,
            doneAt: o.done_at,
            improvementNote: o.improvement_note,
            isRecurring: true as const,
          };
        });
    },
    enabled: !!childId && activeDate !== "all",
  });

  // Realtime subscription
  useEffect(() => {
    if (!childId) return;
    const channel = supabase
      .channel(`recurring-tasks-${childId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "task_occurrences" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["recurring-tasks", childId] });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [childId, queryClient]);

  const completeOccurrence = useMutation({
    mutationFn: async ({ occurrenceId, improvementNote }: { occurrenceId: string; improvementNote?: string }) => {
      if (!childId || !familyId) throw new Error("Missing context");

      const occ = items.find((i) => i.id === occurrenceId);

      const { error } = await supabase
        .from("task_occurrences")
        .update({
          status: "done" as const,
          done_at: new Date().toISOString(),
          improvement_note: improvementNote || null,
        })
        .eq("id", occurrenceId);
      if (error) throw error;

      // Award points if category is set and not "Annet"
      if (occ && occ.category && occ.category !== "Annet") {
        await supabase
          .from("point_transactions")
          .insert({
            child_id: childId,
            family_id: familyId,
            amount: 1,
            reason: "Fullført gjentagende oppgave",
          });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-tasks", childId] });
      queryClient.invalidateQueries({ queryKey: ["points-total", childId] });
    },
  });

  const skipOccurrence = useMutation({
    mutationFn: async (occurrenceId: string) => {
      const { error } = await supabase
        .from("task_occurrences")
        .update({ status: "skipped" as const })
        .eq("id", occurrenceId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-tasks", childId] });
    },
  });

  const startOccurrence = useMutation({
    mutationFn: async (occurrenceId: string) => {
      const { error } = await supabase
        .from("task_occurrences")
        .update({ started_at: new Date().toISOString() })
        .eq("id", occurrenceId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-tasks", childId] });
    },
  });

  const moveOccurrence = useMutation({
    mutationFn: async ({ occurrenceId, fromDate, toDate }: { occurrenceId: string; fromDate: string; toDate: string }) => {
      const occ = items.find((i) => i.id === occurrenceId);
      if (!occ) throw new Error("Occurrence not found");

      const { error: moveErr } = await supabase
        .from("task_occurrences")
        .update({ due_date: toDate })
        .eq("id", occurrenceId);
      if (moveErr) throw moveErr;

      // Upsert skipped occurrence for original date to prevent re-materialization
      const { error: skipErr } = await supabase
        .from("task_occurrences")
        .insert({
          template_id: occ.templateId,
          due_date: fromDate,
          status: "skipped" as const,
        });

      // Ignore unique violation (already exists)
      if (skipErr && skipErr.code !== "23505") throw skipErr;

      return { occurrenceId, fromDate, toDate, templateId: occ.templateId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["recurring-tasks", childId] });

      toast("Oppgave flyttet", {
        action: {
          label: "Angre",
          onClick: async () => {
            await supabase
              .from("task_occurrences")
              .update({ due_date: result.fromDate })
              .eq("id", result.occurrenceId);

            await supabase
              .from("task_occurrences")
              .delete()
              .eq("template_id", result.templateId)
              .eq("due_date", result.fromDate)
              .eq("status", "skipped" as const);

            queryClient.invalidateQueries({ queryKey: ["recurring-tasks", childId] });
          },
        },
      });
    },
  });

  return {
    items,
    isLoading,
    completeOccurrence,
    skipOccurrence,
    startOccurrence,
    moveOccurrence,
  };
}
